'use client';

import { ArrowLeft, ArrowRight, Loader2, Mail, MailCheck } from 'lucide-react';
import Link from 'next/link';
import { useActionState } from 'react';
import { useI18n } from '@/components/i18n-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TRANSLATIONS } from '@/i18n/config';
import { requestPasswordReset, type TForgotPasswordState } from '@/lib/session-actions';

const initialState: TForgotPasswordState = { status: 'idle', email: '' };

export function ForgotPasswordForm() {
  const [state, formAction, pending] = useActionState(requestPasswordReset, initialState);
  const { t } = useI18n();

  const back = (
    <Link
      href="/login"
      className="group flex w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-0.5" />
      {t(TRANSLATIONS.forgot_password.back)}
    </Link>
  );

  if (state.status === 'sent') {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-start gap-3 rounded-lg border border-green-300 bg-green-100 px-4 py-3 text-sm text-green-800" role="status">
          <MailCheck className="mt-0.5 size-4 shrink-0" />
          {/* The same answer whether or not the address has an account. */}
          <span>{t(TRANSLATIONS.forgot_password.sent, { email: state.email })}</span>
        </div>
        {back}
      </div>
    );
  }

  return (
    <form action={formAction} className="flex flex-col gap-5" noValidate>
      <div className="flex flex-col gap-2">
        <Label htmlFor="email">{t(TRANSLATIONS.auth.email)}</Label>
        <div className="relative">
          <Mail className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder={t(TRANSLATIONS.auth.email_placeholder)}
            defaultValue={state.email}
            aria-invalid={state.status === 'error'}
            className="h-11 pl-9"
            required
            autoFocus
          />
        </div>
      </div>

      {state.status === 'error' && (
        <p className="rounded-lg border border-red-300 bg-red-200/60 px-3 py-2.5 text-sm text-red-700" role="alert">
          {t(state.email ? TRANSLATIONS.auth.server_unreachable : TRANSLATIONS.forgot_password.error_email)}
        </p>
      )}

      <Button
        type="submit"
        disabled={pending}
        className="group h-11 bg-linear-to-r from-blue-400 to-blue-600 text-base shadow-lg shadow-blue-400/25 transition-all hover:shadow-blue-400/40 hover:brightness-110"
      >
        {pending ? (
          <>
            <Loader2 className="animate-spin" />
            {t(TRANSLATIONS.forgot_password.sending)}
          </>
        ) : (
          <>
            {t(TRANSLATIONS.forgot_password.send)}
            <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </Button>
      {back}
    </form>
  );
}
